import { Flex, ActionIcon as MActionIcon, Stack, Text, TextInput, ThemeIcon, Tooltip } from "@mantine/core";
import { IIssueApi, IIssueUpdateSchemaProps } from "@schemas/issueSchema";
import { IconExternalLink, IconLock, IconPencil } from "@tabler/icons-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { usePutProjectByProjectIdIssueUpdateSimpleMutation } from "../../../../../../../openapi";
import { ActionIcon, IssueIcon } from "../../../../../../components/CustomIcons/CustomIcons";
import { formatShortDate } from "../../../../admin/selectors";
import { createSimplePayload, guardedIssueUpdate } from "./modalUtils";

export const ModalTitle = ({
  issue,
  projectId,
  showClientLock = true,
  showRegisterInfo = false,
  issueBoard,
}: {
  issue: IIssueApi;
  projectId: string;
  showClientLock?: boolean;
  showRegisterInfo?: boolean;
  issueBoard?: { _id?: string; name?: string } | null;
}) => {
  if (issue == null) {
    return null;
  }

  const [updateValue] = usePutProjectByProjectIdIssueUpdateSimpleMutation();
  const buildSimplePayload = useMemo(() => createSimplePayload(projectId, issue._id), [projectId, issue._id]);

  const updateSimple = useCallback(
    (prop: IIssueUpdateSchemaProps) => {
      guardedIssueUpdate({
        issue,
        prop,
        mutation: updateValue,
        buildPayload: buildSimplePayload,
      });
    },
    [issue, updateValue, buildSimplePayload],
  );

  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const startEditing = useCallback(() => {
    setIsEditing(true);

    // Focus + select on next tick after state/styles apply
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
    });
  }, []);

  const stopEditing = useCallback(() => {
    setIsEditing(false);
  }, []);

  const [inputValue, setInputValue] = useState(issue.title);

  return (
    <Stack gap={0} style={{ minWidth: 0 }}>
      <Flex
        align="center"
        gap={0}
        // Make the whole row a hover target
        className="modal-title-row"
        style={{
          minWidth: 0,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        <ThemeIcon mr="xs" variant="white">
          {issue.taskOrIssue === "TASK" ? <ActionIcon /> : <IssueIcon />}
        </ThemeIcon>
        <Text
          c="var(--mantine-color-mc-primary-6)"
          size="lg"
          style={{
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          {`${issue.id}:`}
        </Text>
        {!isEditing && (
          <Text
            size="lg"
            ml="xs"
            style={{
              minWidth: 0, // allow shrinking
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {issue.title}
          </Text>
        )}
        {isEditing && (
          <TextInput
            ml="xs"
            w="100%"
            ref={inputRef}
            style={{ flex: 1, minWidth: 0 }}
            value={inputValue}
            placeholder="Skriv inn tittel"
            variant="unstyled"
            onBlur={(e) => {
              // commit
              updateSimple({ title: e.target.value });
              stopEditing();
            }}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === "Enter") {
                e.preventDefault();
                const el = e.currentTarget;
                updateSimple({ title: el.value });
                el.blur();
              }
              if (e.key === "Escape") {
                e.preventDefault();
                // revert visual text by forcing blur; since value comes from issue.title,
                // it will snap back on rerender if backend didn't change it.
                e.currentTarget.blur();
              }
            }}
            styles={{
              input: {
                border: "1px solid transparent",
                fontSize: "1.5rem",

                // Disable interaction unless editing
                pointerEvents: isEditing ? "auto" : "none",
                cursor: isEditing ? "text" : "pointer",
              },
            }}
          />
        )}
        {showClientLock === true && issue.clientAccess !== true && (
          <Tooltip label="Låst for kunder">
            <IconLock color="var(--mantine-color-red-6)" size={18} style={{ flexShrink: 0, marginLeft: 8 }} />
          </Tooltip>
        )}
        <MActionIcon
          m="xs"
          variant="subtle"
          aria-label="Rediger tittel"
          onClick={startEditing}
          // Prevent the click from triggering blur on the input in some browsers
          onMouseDown={(e) => e.preventDefault()}
          className="modal-title-edit-icon"
          style={{ flexShrink: 0, visibility: isEditing === true ? "hidden" : "visible", marginLeft: 4 }}
        >
          <IconPencil size={18} />
        </MActionIcon>

        {/* CSS for hover show/hide */}
        <style>
          {`
            .modal-title-edit-icon {
              opacity: 0;
              pointer-events: none;
              transition: opacity 120ms ease;
            }
            .modal-title-row:hover .modal-title-edit-icon,
            .modal-title-row:focus-within .modal-title-edit-icon {
              opacity: 1;
              pointer-events: auto;
            }
          `}
        </style>
      </Flex>

      <UpdatedByInfo issue={issue} />
      {showRegisterInfo === true && issueBoard?._id != null && issueBoard?.name != null && (
        <Flex gap={4} align="center" mt={2} style={{ minWidth: 0 }}>
          <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
            Register:
          </Text>
          <Tooltip zIndex={10000} label={`Gå til ${issueBoard.name}`} withArrow position="bottom">
            <Text
              component="a"
              href={`/#/project/${projectId}/issues/${issueBoard._id}`}
              size="xs"
              fw={600}
              c="var(--mantine-color-mc-primary-6)"
              style={{
                minWidth: 0,
                display: "inline-flex",
                alignItems: "center",
                gap: 2,
                textDecoration: "none",
              }}
            >
              <Text component="span" size="xs" truncate>
                {issueBoard.name}
              </Text>
              <IconExternalLink size={12} style={{ flexShrink: 0 }} />
            </Text>
          </Tooltip>
        </Flex>
      )}
    </Stack>
  );
};

const UpdatedByInfo = ({ issue }: { issue: IIssueApi }) => {
  if (issue == null) {
    return null;
  }

  if (typeof issue.lastUpdatedBy !== "string" && issue.lastUpdatedBy?.name == null) {
    return (
      <Text size="xs" c="dimmed" truncate>
        Opprettet av {typeof issue.createdBy !== "string" && issue.createdBy?.name}, {formatShortDate(issue.createdAt)}
      </Text>
    );
  }
  if (typeof issue.lastUpdatedBy !== "string" && issue.lastUpdatedBy?.name != null && issue.status !== "Fullført") {
    return (
      <Text size="xs" c="dimmed" truncate>
        Oppdatert av {typeof issue.lastUpdatedBy !== "string" && issue.lastUpdatedBy?.name},{" "}
        {formatShortDate(issue.updatedAt)}
      </Text>
    );
  }
  if (issue.status === "Fullført") {
    return (
      <Text size="xs" c="green.7" truncate>
        Fullført {formatShortDate(issue.dates?.completed)}
      </Text>
    );
  }

  return null;
};
